import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import formatDateToStandard from '../utils/DateFormatter';

const EmiInstallmentTable = ({ 
  installments, 
  onStatusUpdate, 
  onTogglePaymentStatus,
  currencySymbol = '$',
  showActions = true 
}) => {
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const getStatusColor = (status, dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    
    switch (status) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'missed':
        return 'error';
      case 'delayed':
        return 'warning';
      case 'pending':
        return due < today ? 'warning' : 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status, dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    
    switch (status) {
      case 'completed':
      case 'paid':
        return <CheckCircleIcon fontSize="small" />;
      case 'missed':
      case 'delayed':
        return <WarningIcon fontSize="small" />;
      case 'pending':
        return due < today ? <WarningIcon fontSize="small" /> : <PaymentIcon fontSize="small" />;
      default:
        return <PaymentIcon fontSize="small" />;
    }
  };

  const handleStatusUpdate = (installment, newStatus) => {
    // Standardize on "completed" status instead of "paid"
    if (newStatus === 'paid') {
      newStatus = 'completed';
    }
    
    const updateData = {
      status: newStatus,
      payment_date: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
    };
    
    onStatusUpdate(installment.id, updateData);
    setActionDialogOpen(false);
    setSelectedInstallment(null);
  };

  const toggleRowExpansion = (installmentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(installmentId)) {
      newExpanded.delete(installmentId);
    } else {
      newExpanded.add(installmentId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    return formatDateToStandard(dateString);
  };

  if (!installments || installments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        No EMI installments found.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Installment #</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Date</TableCell>
              {showActions && <TableCell align="center">Actions</TableCell>}
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {installments.map((installment) => (
              <React.Fragment key={installment.id}>
                <TableRow>
                  <TableCell>{installment.installment_number}</TableCell>
                  <TableCell>{formatDate(installment.due_date)}</TableCell>
                  <TableCell align="right">
                    {currencySymbol}{parseFloat(installment.amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(installment.status, installment.due_date)}
                      label={
                        installment.status === 'paid' ? 'Completed' : 
                        installment.status.charAt(0).toUpperCase() + installment.status.slice(1)
                      }
                      color={getStatusColor(installment.status, installment.due_date)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {installment.payment_date ? formatDate(installment.payment_date) : '-'}
                  </TableCell>
                  {showActions && (
                    <TableCell align="center">
                      {(installment.status === 'pending' || installment.status === 'due') && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => {
                              setSelectedInstallment(installment);
                              setActionDialogOpen(true);
                            }}
                          >
                            Mark Paid
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleStatusUpdate(installment, 'missed')}
                          >
                            Mark Missed
                          </Button>
                        </Box>
                      )}
                      {(installment.status === 'paid' || installment.status === 'completed') && onTogglePaymentStatus && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => onTogglePaymentStatus(installment.id)}
                        >
                          Mark Unpaid
                        </Button>
                      )}
                      {installment.status === 'missed' && onTogglePaymentStatus && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => handleStatusUpdate(installment, 'completed')}
                        >
                          Mark Paid
                        </Button>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(installment.id)}
                    >
                      {expandedRows.has(installment.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={showActions ? 7 : 6}>
                    <Collapse in={expandedRows.has(installment.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Installment Details
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
                          <Typography variant="body2">
                            <strong>Interest Rate:</strong> {installment.interest_rate}% p.a.
                          </Typography>
                          <Typography variant="body2">
                            <strong>Created:</strong> {formatDate(installment.date_created)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Last Modified:</strong> {formatDate(installment.date_modified)}
                          </Typography>
                          {installment.meta && Object.keys(installment.meta).length > 0 && (
                            <Typography variant="body2">
                              <strong>Additional Info:</strong> {JSON.stringify(installment.meta)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark installment #{selectedInstallment?.installment_number} 
            of {currencySymbol}{parseFloat(selectedInstallment?.amount || 0).toFixed(2)} as paid?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: This installment will be marked as completed even if it's before the due date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleStatusUpdate(selectedInstallment, 'completed')}
            variant="contained"
            color="success"
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmiInstallmentTable;
